/**
 * 分享页面组件
 *
 * 通过 URL 中的 token 参数加载并展示分享的照片内容。
 * 支持三种状态：加载中、错误、成功。
 * 成功时展示照片网格和幻灯片播放入口。
 */
import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useShare } from '../hooks/useShare';
import { PhotoGrid } from '../components/PhotoGrid';
import { Slideshow } from '../components/Slideshow';

import styles from './SharePage.module.css';

/**
 * 分享页面
 * 根据 URL 中的 :token 参数获取分享数据并展示
 */
export function SharePage(): React.JSX.Element {
  const { token } = useParams<{ readonly token: string }>();
  const { share, isLoading, error, errorType } = useShare(token);

  // 幻灯片状态
  const [slideshowIndex, setSlideshowIndex] = useState<number | null>(null);

  // 打开幻灯片（从指定索引开始）
  const openSlideshow = useCallback((index: number) => {
    setSlideshowIndex(index);
  }, []);

  // 关闭幻灯片
  const closeSlideshow = useCallback(() => {
    setSlideshowIndex(null);
  }, []);

  // 从第一张开始播放幻灯片
  const playSlideshow = useCallback(() => {
    setSlideshowIndex(0);
  }, []);

  // 加载中状态
  if (isLoading) {
    return (
      <main className={styles.page}>
        <div className={styles.loading}>
          <span className={styles.spinner} role="status" />
          <span>加载中...</span>
        </div>
      </main>
    );
  }

  // 错误状态
  if (error || !share) {
    return (
      <main className={styles.page}>
        <div className={styles.error}>
          <div className={styles.errorIcon} aria-hidden="true">
            {errorType === 'NOT_FOUND' ? '404' : '!'}
          </div>
          <h1 className={styles.errorTitle}>
            {errorType === 'NOT_FOUND' ? '分享不存在' : '加载失败'}
          </h1>
          <p className={styles.errorMessage}>
            {error ?? '未知错误，请稍后重试'}
          </p>
        </div>
      </main>
    );
  }

  // 成功状态
  const photoCount = share.photos.length;
  const displayTitle = share.title ?? '照片分享';

  return (
    <main className={styles.page}>
      <div className={styles.content}>
        {/* 页头信息 */}
        <header className={styles.header}>
          <h1 className={styles.title}>{displayTitle}</h1>
          <div className={styles.meta}>
            <span>{photoCount} 张照片</span>
            <span>{new Date(share.createdAt).toLocaleDateString('zh-CN')}</span>
          </div>
        </header>

        {/* 操作栏 */}
        {photoCount > 0 && (
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.playButton}
              onClick={playSlideshow}
            >
              播放幻灯片
            </button>
          </div>
        )}

        {/* 照片网格 */}
        <PhotoGrid photos={share.photos} onPhotoClick={openSlideshow} />

        {/* 幻灯片覆盖层 */}
        {slideshowIndex !== null && (
          <Slideshow
            photos={share.photos}
            initialIndex={slideshowIndex}
            onClose={closeSlideshow}
          />
        )}
      </div>
    </main>
  );
}
